import { computed } from 'vue';
import { useStore } from 'vuex';
import { getIanaTimezoneNameFromKnackTimezoneName } from '@knack/time-helper';

/**
 * Formats a timezone identifier into a short timezone name
 * @param timezone - The timezone identifier (e.g., 'America/New_York')
 * @returns The short timezone name (e.g., 'EST') or the original timezone on error
 */
function formatTimezoneShort(timezone: string): string {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(({ type }) => type === 'timeZoneName');

    return timeZonePart?.value || timezone;
  } catch (error) {
    return timezone;
  }
}

/**
 * Composable for multi-timezone functionality
 *
 * @returns Object containing multi-timezone state and utilities
 */
export default function useMultiTimezone() {
  const store = useStore();

  /**
   * Determines if multi-timezone functionality is enabled
   */
  const isMultiTimezone = computed(() => store.getters.app.get('settings').isMultiTimezoneEnabled);

  /**
   * Gets the user's local timezone in short format (e.g., 'PST', 'EST')
   */
  const localTimezone = computed(() => {
    const trueTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatTimezoneShort(trueTimezone);
  });

  /**
   * Gets the application's configured timezone in short format
   */
  const appTimezone = computed(() => {
    const knackAppTimezone = store.getters.app.get('settings').timezone;
    const trueTimezone = getIanaTimezoneNameFromKnackTimezoneName(knackAppTimezone);
    if (!trueTimezone) {
      return knackAppTimezone;
    }
    return formatTimezoneShort(trueTimezone);
  });

  return {
    isMultiTimezone,
    localTimezone,
    appTimezone,
  };
}
